import { ProductHits } from "./ProductHits";
import { ProductListStyles, ProductListWrapperStyles } from "./ProductListStyles";
import { Grid } from "@ui/components/layout/grid/Grid";
import { Flex } from "@ui/components/layout/flex/Flex";
import type { FC, ReactElement } from "react";
import { useMemo, useRef } from "react";
import { isEmpty } from "lodash-es";
import { useStore } from "@store/index";
import type { IPartStoryTile } from "@contentful-api/types/contentful";
import { EditorialWidget } from "./EditorialWidget";
import { useCurrentRefinements, useInstantSearch } from "react-instantsearch";
import { WIDGET_POSITIONS } from "./widget-positions";
import { useSmBreakpoint } from "@ui/hooks/useBreakpoint";

interface IProductHitsWrapperProps {
    productListHits: any[];
    footerContent: JSX.Element;
    noResultsContent: string | ReactElement;
    shouldSaveLastVisitedPath?: boolean;
    productListName: string;
    widgetSize?: string;
    editorialWidgets?: IPartStoryTile[];
}

export const ProductHitsWrapper: FC<IProductHitsWrapperProps> = ({
    productListHits,
    footerContent,
    noResultsContent,
    shouldSaveLastVisitedPath = false,
    productListName,
    widgetSize,
    editorialWidgets,
}) => {
    const ref = useRef(null);
    // We will show widgets only when filters are not applied to avoid re-calculations on the layouts.
    const { items: filtersAreApplied } = useCurrentRefinements();
    // We don't want the debounce, otherwise will casue layout shift
    const sm = useSmBreakpoint();

    const lastVisitedPlpContainerHeight = useStore(
        (state) => state.data.products?.lastVisitedPlpContainerHeight
    );
    const lastVisitedPlp = useStore((state) => state.data.products?.lastVisitedPlp);
    const lastLoadedPage = useStore((state) => state.data.products?.lastLoadedPage);

    const computeWidgetPosition = useMemo(
        () =>
            (products: number, index: number): object => {
                if (!widgetSize) {
                    return null;
                }

                const config = WIDGET_POSITIONS[widgetSize];
                if (!config) return null;

                if (index === 0 && products >= config.first.minProducts[sm ? 1 : 0]) {
                    return config.first.position;
                }

                if (index === 1 && products >= config.second.minProducts[sm ? 1 : 0]) {
                    return config.second.position;
                }

                return null;
            },
        [widgetSize, sm]
    );

    const { results } = useInstantSearch();

    return !isEmpty(productListHits) ? (
        <Flex sx={{ ...ProductListWrapperStyles }} data-testid="product-list-grid">
            <Grid
                sx={ProductListStyles(isEmpty(editorialWidgets?.length))}
                ref={ref}
                minH={
                    lastVisitedPlp &&
                    window?.location?.pathname + window.location.search === lastVisitedPlp
                        ? `${lastVisitedPlpContainerHeight}px`
                        : "auto"
                }
            >
                <ProductHits
                    productListHits={productListHits}
                    shouldSaveLastVisitedPath={shouldSaveLastVisitedPath}
                    productListName={productListName}
                    containerHeight={lastLoadedPage > 1 ? ref?.current?.clientHeight : undefined}
                />
                {!!editorialWidgets &&
                    editorialWidgets.map(
                        (editorialWidget, index) =>
                            editorialWidget.fields.image && (
                                <EditorialWidget
                                    image={editorialWidget.fields.image}
                                    title={editorialWidget.fields.title}
                                    styles={computeWidgetPosition(productListHits.length, index)}
                                    link={editorialWidget.fields.link}
                                    widgetSize={widgetSize}
                                    isVisible={isEmpty(filtersAreApplied)}
                                    key={editorialWidget.sys.id}
                                />
                            )
                    )}
            </Grid>
            {productListHits?.length > 0 && footerContent}
        </Flex>
    ) : (
        <Flex
            sx={{ fontWeight: "bold", mx: [3, 3, 6, 6], my: [10, 10, 16, 16] }}
            data-testid="emptyProductHits,productHits"
        >
            {results.__isArtificial ? "" : noResultsContent}
        </Flex>
    );
};
